import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  CheckCircle, Clock, Heart, MessageCircle, AlertTriangle,
  Flag, Send, Star, User, ThumbsUp, ArrowLeft
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverPostCall = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [sessionInfo, setSessionInfo] = useState(null);
  const [weeklyQuestion, setWeeklyQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  const getToken = () => localStorage.getItem('observer_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadForm();
  }, [navigate, sessionId]);

  const loadForm = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/observer/postcall/form`, {
        params: { token, session_id: sessionId }
      });
      setFormFields(response.data.form_fields || []);
      setSessionInfo(response.data.session);
      setWeeklyQuestion(response.data.weekly_question);
      
      // Initialize form data with defaults
      const defaults = {};
      response.data.form_fields?.forEach(field => {
        if (field.type === 'boolean') {
          defaults[field.id] = false;
        } else if (field.type === 'multiselect') {
          defaults[field.id] = [];
        } else if (field.type === 'select') {
          defaults[field.id] = '';
        } else {
          defaults[field.id] = '';
        }
      });
      setFormData(defaults);
    } catch (error) {
      console.error('Error loading form:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const toggleMultiSelect = (fieldId, option) => {
    setFormData(prev => {
      const current = prev[fieldId] || [];
      if (current.includes(option)) {
        return { ...prev, [fieldId]: current.filter(o => o !== option) };
      }
      return { ...prev, [fieldId]: [...current, option] };
    });
  };

  const isFieldVisible = (field) => {
    if (!field.conditional) return true;
    
    const conditionField = field.conditional.field;
    const conditionValue = field.conditional.value;
    const conditionValueNot = field.conditional.value_not;
    
    if (conditionValue !== undefined) {
      return formData[conditionField] === conditionValue;
    }
    if (conditionValueNot !== undefined) {
      return formData[conditionField] !== conditionValueNot;
    }
    return true;
  };

  const validateForm = () => {
    const requiredFields = formFields.filter(f => f.required && isFieldVisible(f));
    const missingFields = requiredFields.filter(f => {
      const value = formData[f.id];
      if (f.type === 'multiselect') return !value || value.length === 0;
      if (f.type === 'boolean') return value === undefined;
      return !value;
    });
    return missingFields;
  };

  const submitForm = async () => {
    const missing = validateForm();
    if (missing.length > 0) {
      alert(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/observer/postcall/submit`, formData, {
        params: { token, session_id: sessionId }
      });
      
      navigate('/observer/sessions', { 
        state: { message: 'Session logged successfully!' }
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const getMoodEmoji = (mood) => {
    const emojis = {
      very_happy: '😄',
      happy: '😊',
      neutral: '😐',
      sad: '😔',
      very_sad: '😢'
    };
    return emojis[mood] || '';
  };

  const getMoodLabel = (mood) => {
    return mood?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
  };

  const renderField = (field) => {
    if (!isFieldVisible(field)) return null;

    switch (field.type) {
      case 'number':
        return (
          <Input
            type="number"
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, parseInt(e.target.value) || '')}
            min={field.min}
            max={field.max}
            className="w-32"
          />
        );

      case 'select':
        if (field.id.includes('mood')) {
          return (
            <div className="flex flex-wrap gap-2">
              {field.options.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => updateField(field.id, option)}
                  className={`px-4 py-3 rounded-xl border-2 transition-all ${
                    formData[field.id] === option
                      ? 'border-blue-500 bg-blue-50 scale-105'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl block">{getMoodEmoji(option)}</span>
                  <span className="text-xs text-gray-600 mt-1 block">{getMoodLabel(option)}</span>
                </button>
              ))}
            </div>
          );
        }
        if (field.id === 'flag_level') {
          return (
            <div className="flex flex-wrap gap-2">
              {field.options.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => updateField(field.id, option)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    formData[field.id] === option
                      ? option === 'red' ? 'border-red-500 bg-red-50' :
                        option === 'orange' ? 'border-orange-500 bg-orange-50' :
                        option === 'yellow' ? 'border-yellow-500 bg-yellow-50' :
                        'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {option === 'none' ? '✓ None' :
                   option === 'yellow' ? '🟡 Yellow' :
                   option === 'orange' ? '🟠 Orange' :
                   '🔴 Red'}
                </button>
              ))}
            </div>
          );
        }
        return (
          <select
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            className="w-full border rounded-lg p-2"
          >
            <option value="">Select...</option>
            {field.options.map(option => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-2">
            {field.options.map(option => (
              <button
                key={option}
                type="button"
                onClick={() => toggleMultiSelect(field.id, option)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  formData[field.id]?.includes(option)
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </button>
            ))}
          </div>
        );

      case 'boolean':
        return (
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => updateField(field.id, true)}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                formData[field.id] === true
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <ThumbsUp className="w-4 h-4 inline mr-2" />
              Yes
            </button>
            <button
              type="button"
              onClick={() => updateField(field.id, false)}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                formData[field.id] === false
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              No
            </button>
          </div>
        );

      case 'textarea':
        return (
          <Textarea
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
            rows={3}
            maxLength={field.max_length}
          />
        );

      default:
        return (
          <Input
            value={formData[field.id] || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
          />
        );
    }
  };

  // Group fields by step
  const getFieldsForStep = (stepNum) => {
    const groups = {
      1: ['session_duration', 'child_mood_start', 'child_mood_end', 'engagement_level'],
      2: ['topics_discussed', 'weekly_question_asked', 'weekly_question_response', 'highlight'],
      3: ['concerns', 'flag_level', 'flag_reason', 'follow_up_needed', 'follow_up_notes', 'parent_update_suggestion']
    };
    return formFields.filter(f => groups[stepNum]?.includes(f.id));
  };

  const stepTitles = {
    1: 'Session Details',
    2: 'Conversation',
    3: 'Review & Flags'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" data-testid="postcall-page">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Post-Call Form</h1>
            <p className="text-gray-600">Log your session details</p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <button
                    onClick={() => setStep(s)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      step === s
                        ? 'bg-blue-500 text-white scale-110'
                        : step > s
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                  </button>
                  {s < 3 && (
                    <div className={`w-12 h-1 rounded ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {step === 1 && <Clock className="w-5 h-5 text-blue-500" />}
                {step === 2 && <MessageCircle className="w-5 h-5 text-purple-500" />}
                {step === 3 && <Flag className="w-5 h-5 text-red-500" />}
                {stepTitles[step]}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Weekly Question Reminder (Step 2) */}
              {step === 2 && weeklyQuestion && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
                  <p className="text-sm font-medium text-purple-700 mb-1">This Week's Question:</p>
                  <p className="text-purple-900 italic">"{weeklyQuestion}"</p>
                </div>
              )}

              {/* Form Fields */}
              {getFieldsForStep(step).map((field) => {
                if (!isFieldVisible(field)) return null;
                
                return (
                  <div key={field.id} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderField(field)}
                  </div>
                );
              })}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {step > 1 ? 'Previous' : 'Cancel'}
                </Button>
                
                {step < 3 ? (
                  <Button onClick={() => setStep(step + 1)}>
                    Next
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={submitForm}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="submit-postcall-btn"
                  >
                    {submitting ? 'Submitting...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Session
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Flag Level Explanation (Step 3) */}
          {step === 3 && (
            <Card className="mt-4 bg-gray-50">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Flag Level Guide:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>🟢 <strong>None</strong> - Normal session, no concerns</li>
                  <li>🟡 <strong>Yellow</strong> - Monitor: low mood, avoidance (24h SLA)</li>
                  <li>🟠 <strong>Orange</strong> - Concern: keywords like hurt, scared (2h SLA)</li>
                  <li>🔴 <strong>Red</strong> - Critical: abuse, safety risk (15min SLA)</li>
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverPostCall;
